import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  ActivityIndicator,
  Alert,
  Switch,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { RootStackNavigationProp, RootStackParamList } from '../../../../types/navigation';
import { petCarePreferencesService } from '../../../../services/petCarePreferencesInfo';

import { createLogger } from '../../../../utils/logger';
const logger = createLogger('PetCarePreferencesInput');

type PetCarePreferencesInputScreenRouteProp = RouteProp<RootStackParamList, 'PetCarePreferencesInput'>;

export const PetCarePreferencesInputScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<PetCarePreferencesInputScreenRouteProp>();

  const { petCarePreferencesData, isEditing } = route.params || {};

  // Primary Caretaker Fields
  const [caretakerName, setCaretakerName] = useState(
    petCarePreferencesData?.caretakerName || ''
  );
  const [caretakerRelationship, setCaretakerRelationship] = useState(
    petCarePreferencesData?.caretakerRelationship || ''
  );
  const [caretakerPhone, setCaretakerPhone] = useState(
    petCarePreferencesData?.caretakerPhone || ''
  );
  const [caretakerEmail, setCaretakerEmail] = useState(
    petCarePreferencesData?.caretakerEmail || ''
  );

  // Alternate Caretaker Fields
  const [alternateCaretakerName, setAlternateCaretakerName] = useState(
    petCarePreferencesData?.alternateCaretakerName || ''
  );
  const [alternateCaretakerRelationship, setAlternateCaretakerRelationship] = useState(
    petCarePreferencesData?.alternateCaretakerRelationship || ''
  );
  const [alternateCaretakerPhone, setAlternateCaretakerPhone] = useState(
    petCarePreferencesData?.alternateCaretakerPhone || ''
  );
  const [alternateCaretakerEmail, setAlternateCaretakerEmail] = useState(
    petCarePreferencesData?.alternateCaretakerEmail || ''
  );

  // Other Fields
  const [feedingInstructions, setFeedingInstructions] = useState(
    petCarePreferencesData?.feedingInstructions || ''
  );
  const [medicationInstructions, setMedicationInstructions] = useState(
    petCarePreferencesData?.medicationInstructions || ''
  );
  const [veterinarianName, setVeterinarianName] = useState(
    petCarePreferencesData?.veterinarianName || ''
  );
  const [veterinarianPhone, setVeterinarianPhone] = useState(
    petCarePreferencesData?.veterinarianPhone || ''
  );
  const [veterinarianAddress, setVeterinarianAddress] = useState(
    petCarePreferencesData?.veterinarianAddress || ''
  );
  const [petInsuranceInfo, setPetInsuranceInfo] = useState(
    petCarePreferencesData?.petInsuranceInfo || ''
  );
  const [specialInstructions, setSpecialInstructions] = useState(
    petCarePreferencesData?.specialInstructions || ''
  );
  const [notes, setNotes] = useState(
    petCarePreferencesData?.notes || ''
  );
  const [saving, setSaving] = useState(false);

  // Phone number formatting function
  const formatPhoneNumber = (text: string) => {
    // Remove all non-numeric characters
    const numbers = text.replace(/\D/g, '');

    // Format as (xxx) xxx-xxxx
    if (numbers.length <= 3) {
      return numbers;
    } else if (numbers.length <= 6) {
      return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
    } else {
      return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
    }
  };

  // Email validation function
  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSave = async () => {
    // Validation
    if (!caretakerName.trim()) {
      Alert.alert('Error', 'Please enter the primary caretaker name');
      return;
    }

    if (!caretakerRelationship.trim()) {
      Alert.alert('Error', 'Please enter the primary caretaker relationship');
      return;
    }

    if (!caretakerPhone.trim()) {
      Alert.alert('Error', 'Please enter the primary caretaker phone number');
      return;
    }

    if (!caretakerEmail.trim()) {
      Alert.alert('Error', 'Please enter the primary caretaker email');
      return;
    }

    if (!isValidEmail(caretakerEmail)) {
      Alert.alert('Error', 'Please enter a valid email address for the primary caretaker');
      return;
    }

    if (alternateCaretakerEmail && !isValidEmail(alternateCaretakerEmail)) {
      Alert.alert('Error', 'Please enter a valid email address for the alternate caretaker');
      return;
    }

    try {
      setSaving(true);

      const petCarePreferencesUpdateData = {
        caretakerName: caretakerName.trim(),
        caretakerRelationship: caretakerRelationship.trim(),
        caretakerPhone: caretakerPhone.trim(),
        caretakerEmail: caretakerEmail.trim(),
        alternateCaretakerName: alternateCaretakerName.trim(),
        alternateCaretakerRelationship: alternateCaretakerRelationship.trim(),
        alternateCaretakerPhone: alternateCaretakerPhone.trim(),
        alternateCaretakerEmail: alternateCaretakerEmail.trim(),
        feedingInstructions: feedingInstructions.trim(),
        medicationInstructions: medicationInstructions.trim(),
        veterinarianName: veterinarianName.trim(),
        veterinarianPhone: veterinarianPhone.trim(),
        veterinarianAddress: veterinarianAddress.trim(),
        petInsuranceInfo: petInsuranceInfo.trim(),
        specialInstructions: specialInstructions.trim(),
        notes: notes.trim(),
        lastUpdated: new Date().toISOString(),
      };

      let savedPetCarePreferences;

      if (isEditing && petCarePreferencesData?.id) {
        // Update existing record
        savedPetCarePreferences = await petCarePreferencesService.updatePetCarePreferences(
          petCarePreferencesData.id,
          petCarePreferencesUpdateData
        );
        // Handle wrapped response format { success, data, message }
        const updatedData = savedPetCarePreferences?.data || savedPetCarePreferences;
        logger.debug('Pet care preferences updated successfully with ID:', updatedData.id);

        // Navigate to lock/unlock screen to allow re-selection
        navigation.navigate('PetCarePreferencesLockUnlock', { petCarePreferencesId: updatedData.id });
      } else {
        // Create new record
        savedPetCarePreferences = await petCarePreferencesService.createPetCarePreferences(petCarePreferencesUpdateData);
        // Handle wrapped response format { success, data, message }
        const createdData = savedPetCarePreferences?.data || savedPetCarePreferences;
        logger.debug('Pet care preferences saved successfully with ID:', createdData.id);

        // Navigate to lock/unlock screen with the ID
        navigation.navigate('PetCarePreferencesLockUnlock', { petCarePreferencesId: createdData.id });
      }
    } catch (error) {
      logger.error('Error saving pet care preferences:', error);
      Alert.alert('Error', 'Failed to save pet care preferences. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={[styles.backIcon, { tintColor: '#fff' }]}
          />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image
          source={require('../../../../assets/icons/keyimage.png')}
          style={styles.backgroundKey}
        />
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <Text style={styles.title}>
          Add Your Pet Care Preferences
        </Text>
      </View>

      <View style={styles.inputContainer}>
        <ScrollView style={styles.scrollView}>
          {/* Primary Caretaker Section */}
          <Text style={styles.sectionTitle}>Primary Pet Caretaker:</Text>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Full Name:</Text>
            <TextInput
              style={styles.input}
              value={caretakerName}
              onChangeText={setCaretakerName}
              placeholder="Enter full name of primary caretaker"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Relationship:</Text>
            <TextInput
              style={styles.input}
              value={caretakerRelationship}
              onChangeText={setCaretakerRelationship}
              placeholder="e.g., Family Member, Friend, Neighbor"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Phone Number:</Text>
            <TextInput
              style={styles.input}
              value={caretakerPhone}
              onChangeText={(text) => setCaretakerPhone(formatPhoneNumber(text))}
              placeholder="(xxx) xxx-xxxx"
              placeholderTextColor="#A9A9A9"
              keyboardType="phone-pad"
              maxLength={14}
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Email Address:</Text>
            <TextInput
              style={styles.input}
              value={caretakerEmail}
              onChangeText={setCaretakerEmail}
              placeholder="caretaker@example.com"
              placeholderTextColor="#A9A9A9"
              keyboardType="email-address"
              autoCapitalize="none"
            />
          </View>

          {/* Alternate Caretaker Section */}
          <Text style={styles.sectionTitle}>Alternate Pet Caretaker (Optional):</Text>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Full Name:</Text>
            <TextInput
              style={styles.input}
              value={alternateCaretakerName}
              onChangeText={setAlternateCaretakerName}
              placeholder="Enter full name of alternate caretaker"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Relationship:</Text>
            <TextInput
              style={styles.input}
              value={alternateCaretakerRelationship}
              onChangeText={setAlternateCaretakerRelationship}
              placeholder="e.g., Family Member, Friend, Neighbor"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Phone Number:</Text>
            <TextInput
              style={styles.input}
              value={alternateCaretakerPhone}
              onChangeText={(text) => setAlternateCaretakerPhone(formatPhoneNumber(text))}
              placeholder="(xxx) xxx-xxxx"
              placeholderTextColor="#A9A9A9"
              keyboardType="phone-pad"
              maxLength={14}
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Email Address:</Text>
            <TextInput
              style={styles.input}
              value={alternateCaretakerEmail}
              onChangeText={setAlternateCaretakerEmail}
              placeholder="alternate@example.com"
              placeholderTextColor="#A9A9A9"
              keyboardType="email-address"
              autoCapitalize="none"
            />
          </View>

          {/* Pet Care Details Section */}
          <Text style={styles.sectionTitle}>Pet Care Information:</Text>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Feeding Instructions:</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={feedingInstructions}
              onChangeText={setFeedingInstructions}
              placeholder="Detailed feeding instructions for your pets"
              placeholderTextColor="#A9A9A9"
              multiline={true}
              numberOfLines={4}
              textAlignVertical="top"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Medication Instructions:</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={medicationInstructions}
              onChangeText={setMedicationInstructions}
              placeholder="Detailed medication instructions for your pets"
              placeholderTextColor="#A9A9A9"
              multiline={true}
              numberOfLines={4}
              textAlignVertical="top"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Veterinarian Name:</Text>
            <TextInput
              style={styles.input}
              value={veterinarianName}
              onChangeText={setVeterinarianName}
              placeholder="Name of the veterinarian"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Veterinarian Phone:</Text>
            <TextInput
              style={styles.input}
              value={veterinarianPhone}
              onChangeText={(text) => setVeterinarianPhone(formatPhoneNumber(text))}
              placeholder="(xxx) xxx-xxxx"
              placeholderTextColor="#A9A9A9"
              keyboardType="phone-pad"
              maxLength={14}
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Veterinarian Address:</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={veterinarianAddress}
              onChangeText={setVeterinarianAddress}
              placeholder="Address of the veterinarian"
              placeholderTextColor="#A9A9A9"
              multiline={true}
              numberOfLines={3}
              textAlignVertical="top"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Pet Insurance Information:</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={petInsuranceInfo}
              onChangeText={setPetInsuranceInfo}
              placeholder="Pet insurance information"
              placeholderTextColor="#A9A9A9"
              multiline={true}
              numberOfLines={3}
              textAlignVertical="top"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Special Instructions:</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={specialInstructions}
              onChangeText={setSpecialInstructions}
              placeholder="Special care instructions for your pets"
              placeholderTextColor="#A9A9A9"
              multiline={true}
              numberOfLines={4}
              textAlignVertical="top"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Additional Notes:</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={notes}
              onChangeText={setNotes}
              placeholder="Additional notes for pet care"
              placeholderTextColor="#A9A9A9"
              multiline={true}
              numberOfLines={4}
              textAlignVertical="top"
            />
          </View>

          <View style={styles.submitButtonContainer}>
            <TouchableOpacity
              style={[styles.saveButton, saving && { opacity: 0.7 }]}
              onPress={handleSave}
              disabled={saving}
            >
              {saving ? (
                <ActivityIndicator color="#FFFFFF" />
              ) : (
                <Text style={styles.saveButtonText}>{isEditing ? 'Update' : 'Submit'}</Text>
              )}
            </TouchableOpacity>
          </View>

          <View style={styles.bottomPadding} />
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: ms(15),
    paddingTop: ms(25),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    padding: ms(15),
    paddingTop: ms(50),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#fff',
  },
  topSection: {
    alignItems: 'center',
    paddingVertical: ms(20),
  },
  backgroundKey: {
    position: 'absolute',
    width: '85%',
    height: '85%',
    opacity: 0.1,
  },
  logo: {
    width: s(125),
    height: s(125),
    marginBottom: ms(16),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: ms(8),
    paddingHorizontal: ms(30),
    textAlign: 'center',
  },
  inputContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(20),
  },
  scrollView: {
    paddingHorizontal: ms(16),
    paddingBottom: ms(120),
  },
  sectionTitle: {
    fontSize: ms(20, 0.3),
    fontWeight: 'bold',
    color: '#F2994A',
    marginBottom: ms(20),
    marginTop: ms(20),
  },
  inputGroup: {
    marginBottom: ms(16),
  },
  label: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#6B7280',
    marginBottom: ms(8),
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(25),
    padding: ms(15),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  textArea: {
    minHeight: s(80),
    textAlignVertical: 'top',
  },
  switchContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginVertical: ms(20),
    paddingHorizontal: ms(5),
  },
  switchLabel: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#6B7280',
    flex: 1,
    marginRight: ms(15),
  },
  submitButtonContainer: {
    alignItems: 'center',
    marginTop: ms(20),
    marginBottom: ms(40),
  },
  saveButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(60),
    alignItems: 'center',
    marginBottom: ms(120),
    minWidth: s(200),
  },
  saveButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  bottomPadding: {
    height: s(120),
  },
});